import { useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Download, Share2, Copy } from "lucide-react";

// Brand marks (kept as inline SVG so we don't add a new dependency and to
// preserve the vintage feel — each mark is monochrome, tinted with
// `currentColor`).
const SocialIcon = ({ kind }) => {
  const paths = {
    instagram: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="1.8"/>
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
        <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor"/>
      </>
    ),
    facebook: (
      <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.25-1.5 1.55-1.5h1.66V4.6c-.3-.05-1.3-.13-2.45-.13-2.42 0-4.06 1.47-4.06 4.18v2.33H7.5V14h2.7v8h3.3z" fill="currentColor"/>
    ),
    x: (
      <path d="M17.5 3h3.2l-7 8 8.3 10h-6.5l-5.1-6.5L4.5 21H1.3l7.5-8.6L1 3h6.7l4.6 6.1L17.5 3z" fill="currentColor"/>
    ),
    whatsapp: (
      <path d="M20 12a8 8 0 0 1-11.9 7L4 20l1.1-4A8 8 0 1 1 20 12zm-8-6a6 6 0 0 0-5.1 9.1l-.6 2.2 2.3-.6A6 6 0 1 0 12 6zm3.4 8.3c-.2-.1-1-.5-1.2-.6-.2-.1-.3 0-.4.2-.1.2-.5.5-.6.7-.1.1-.2.1-.4 0-1.1-.5-1.8-.9-2.4-1.9-.2-.3.2-.3.5-1 .05-.1 0-.2 0-.3-.05-.1-.4-1-.6-1.3-.15-.35-.3-.3-.4-.3h-.4c-.1 0-.3 0-.5.2-.15.2-.7.7-.7 1.7 0 1 .7 2 .8 2.2.1.15 1.35 2.05 3.3 2.85 1.9.75 1.9.5 2.25.45.4-.05 1.2-.5 1.35-.95.15-.5.15-.9.1-1 0-.1-.15-.15-.35-.25z" fill="currentColor"/>
    ),
    tiktok: (
      <path d="M14 3h2.5c.15 1.5 1.05 2.85 2.85 3.35v2.5c-1.1 0-2.15-.3-3.05-.85V15c0 3.05-2.45 5.5-5.5 5.5S5.3 18.05 5.3 15c0-3.05 2.45-5.5 5.5-5.5.35 0 .7.05 1.05.1v2.5a3 3 0 1 0 2.15 2.9V3z" fill="currentColor"/>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">{paths[kind]}</svg>
  );
};

// The `api` client base is already `${BASE}/api`, so paths coming back from
// the backend that start with `/api/` would double-prefix. Normalise here.
const absBadgeUrl = (badgePath) => (
  badgePath.startsWith("http") ? badgePath : `${API}${badgePath.replace(/^\/api/, "")}`
);

// Fetches the badge PNG through the authenticated session (cookies) so we
// can attach it as a File in Web Share API on mobile. Returns null on any
// failure — desktop fallback still works with the plain text intent.
async function fetchBadgeAsFile(badgePath, city) {
  try {
    const r = await fetch(absBadgeUrl(badgePath), { credentials: "include" });
    if (!r.ok) return null;
    const blob = await r.blob();
    return new File([blob], `hospitalityhub-badge-${city || "tesoro"}.png`, { type: "image/png" });
  } catch { return null; }
}

/**
 * BadgeShareButtons — Instagram / Facebook / X / WhatsApp / TikTok row plus
 * a "Scarica PNG" download and a "Copia link" fallback. Every share carries
 * a call-to-action deeplink back to https://hostitalyhub.com so the viral
 * loop is closed.
 */
export default function BadgeShareButtons({ badgeUrl, city, huntTitle, shareUrl, huntId, IT }) {
  const [busy, setBusy] = useState(null);
  const home = "https://hostitalyhub.com";
  const caption = IT
    ? `🏆 Ho appena completato la Mappa del Tesoro "${huntTitle}" a ${city} su HospitalityHub! Prova anche tu → ${home}`
    : `🏆 I just completed the "${huntTitle}" Treasure Map in ${city} on HospitalityHub! Join the adventure → ${home}`;
  const enc = encodeURIComponent(caption);
  const encUrl = encodeURIComponent(shareUrl || home);

  // Fire-and-forget analytics — one row per social button press so the
  // superuser dashboard can rank channels by real click volume.
  const trackShare = (platform) => {
    api.post("/hunt-shares", { hunt_id: huntId || null, platform }).catch(() => {});
  };

  const nativeShare = async (platform) => {
    const canFileShare = navigator.canShare && navigator.share;
    if (canFileShare) {
      setBusy(platform);
      try {
        const file = await fetchBadgeAsFile(badgeUrl, city);
        const payload = { text: caption, url: shareUrl || home };
        if (file && navigator.canShare({ files: [file] })) payload.files = [file];
        await navigator.share(payload);
        return true;
      } catch (e) {
        // AbortError = user cancelled. Anything else falls through to the
        // platform-specific web intent below.
        if (e && e.name === "AbortError") return true;
      } finally { setBusy(null); }
    }
    return false;
  };

  const openIntent = (url) => {
    window.open(url, "_blank", "noopener,noreferrer,width=680,height=640");
  };

  const download = async () => {
    setBusy("download");
    try {
      const file = await fetchBadgeAsFile(badgeUrl, city);
      if (!file) throw new Error("fetch failed");
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(file);
      a.href = objectUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success(IT ? "Badge scaricato" : "Badge downloaded");
    } catch { toast.error(IT ? "Download fallito" : "Download failed"); }
    finally { setBusy(null); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success(IT ? "Testo copiato" : "Text copied");
    } catch { toast.error(IT ? "Copia non riuscita" : "Copy failed"); }
  };

  const buttons = [
    {
      key: "instagram", label: "Instagram", cls: "bg-pink-600 hover:bg-pink-700",
      onClick: async () => {
        trackShare("instagram");
        // Instagram doesn't accept web-intent posts. Download the badge and
        // open the app so the user can attach it manually. Web Share API
        // covers this cleanly on iOS/Android.
        const ok = await nativeShare("instagram");
        if (ok) return;
        await download();
        window.open("https://www.instagram.com/", "_blank", "noopener");
        toast.info(IT ? "Immagine scaricata: aprila su Instagram" : "Image saved: open Instagram to post");
      },
    },
    {
      key: "facebook", label: "Facebook", cls: "bg-[#1877f2] hover:bg-[#0f5fd3]",
      onClick: async () => {
        trackShare("facebook");
        const ok = await nativeShare("facebook");
        if (ok) return;
        openIntent(`https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${enc}`);
      },
    },
    {
      key: "x", label: "X", cls: "bg-black hover:bg-neutral-800",
      onClick: async () => {
        trackShare("x");
        const ok = await nativeShare("x");
        if (ok) return;
        openIntent(`https://twitter.com/intent/tweet?text=${enc}&url=${encUrl}`);
      },
    },
    {
      key: "whatsapp", label: "WhatsApp", cls: "bg-[#25D366] hover:bg-[#1eb85a] text-white",
      onClick: async () => {
        trackShare("whatsapp");
        const ok = await nativeShare("whatsapp");
        if (ok) return;
        openIntent(`https://wa.me/?text=${enc}`);
      },
    },
    {
      key: "tiktok", label: "TikTok", cls: "bg-black hover:bg-neutral-800",
      onClick: async () => {
        trackShare("tiktok");
        const ok = await nativeShare("tiktok");
        if (ok) return;
        await download();
        window.open("https://www.tiktok.com/upload", "_blank", "noopener");
        toast.info(IT ? "Immagine scaricata: caricala su TikTok" : "Image saved: upload it on TikTok");
      },
    },
  ];

  return (
    <div className="space-y-2" data-testid={`badge-share-${city || "hunt"}`}>
      <div className="flex flex-wrap gap-1.5">
        {buttons.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={b.onClick}
            disabled={busy === b.key}
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-full text-white transition-colors ${b.cls} disabled:opacity-60`}
            data-testid={`badge-share-${b.key}`}
            aria-label={`${IT ? "Condividi su" : "Share on"} ${b.label}`}
          >
            <SocialIcon kind={b.key}/> {b.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => { trackShare("download"); download(); }}
          disabled={busy === "download"}
          className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
          data-testid="badge-download"
        >
          <Download className="w-3.5 h-3.5"/> {IT ? "Scarica PNG" : "Download PNG"}
        </button>
        <button
          type="button"
          onClick={() => { trackShare("copy"); copyLink(); }}
          className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
          data-testid="badge-copy"
        >
          <Copy className="w-3.5 h-3.5"/> {IT ? "Copia testo" : "Copy text"}
        </button>
        {navigator.share && (
          <button
            type="button"
            onClick={async () => { trackShare("native"); await nativeShare("native"); }}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
            data-testid="badge-native-share"
          >
            <Share2 className="w-3.5 h-3.5"/> {IT ? "Altro…" : "More…"}
          </button>
        )}
      </div>
    </div>
  );
}
